'use client';

import React from 'react';
import Link from 'next/link';
import { Container, Button } from '@mantine/core';
import classes from './CompanyUpdatesPanel.module.scss';
import { useAOS } from '@/src/lib/hooks/useAOS';
import { AppButton } from '@/src/components/AppButton/AppButton';

export type CompanyUpdateItem = {
    id: string;
    title: string;
    description: string;
    date?: string;
    tag?: string;
};

export type CompanyUpdatesPanelSlice = {
    type: 'company-updates-panel';

    title?: string;
    description?: string;

    updates: CompanyUpdateItem[];

    ctaLabel?: string;
    ctaUrl?: string;

    position?: 'left' | 'right';
    className?: string;

    /** background image for this component only */
    backgroundImageUrl?: string;

    aos?: {
        animation?: string;
        delay?: number;
    };
};

export default function CompanyUpdatesPanel({ slice }: { slice: CompanyUpdatesPanelSlice }) {
    useAOS();

    const {
        title = 'Actualités',
        description = 'Les dernières mises à jour de l’entreprise.',
        updates,
        ctaLabel,
        ctaUrl,
        position = 'right',
        className,
        backgroundImageUrl,
        aos,
    } = slice;

    if (!updates?.length) return null;

    return (
        <section className={[classes.section, className].filter(Boolean).join(' ')} data-side={position}>
            {/* Background for THIS component only (fixed, behind) */}
            {backgroundImageUrl && (
                <div className={classes.bg} aria-hidden="true">
                    <div className={classes.bgImage} style={{ backgroundImage: `url(${backgroundImageUrl})` }} />
                    <div className={classes.bgOverlay} />
                </div>
            )}

            <Container size="xl" className={classes.container}>
                <div
                    className={classes.panel}
                    data-aos={aos?.animation ?? 'fade-up'}
                    data-aos-delay={typeof aos?.delay === 'number' ? aos.delay : 0}
                >
                    <div className={classes.header}>
                        <div className={classes.heading}>
                            <h2 className={classes.title}>{title}</h2>
                            {description && <p className={classes.subtitle}>{description}</p>}
                        </div>

                        {ctaLabel && ctaUrl && (
                            <AppButton component={Link} href={ctaUrl} className={classes.cta}>
                                {ctaLabel}
                            </AppButton>
                        )}
                    </div>

                    <div className={classes.list} role="list">
                        {updates.slice(0, 6).map((u, idx) => (
                            <article
                                key={u.id}
                                className={classes.item}
                                role="listitem"
                                data-aos="fade-up"
                                data-aos-delay={Math.min(90 + idx * 60, 360)}
                            >
                                <div className={classes.itemTop}>
                                    {u.tag && <span className={classes.tag}>{u.tag}</span>}
                                    {u.date && <time className={classes.date}>{u.date}</time>}
                                </div>

                                <h3 className={classes.itemTitle}>{u.title}</h3>
                                <p className={classes.itemDesc}>{u.description}</p>
                            </article>
                        ))}
                    </div>
                </div>
            </Container>
        </section>
    );
}
